import { createTheme } from '@material-ui/core/styles';
import tokens from './tokens';

/**
 * Theme for the Material UI v4 (`@material-ui/core`) components.
 *
 * About a hundred screens and components still use v4, and until now they
 * had no theme at all -- they fell back to v4's defaults (Roboto, default
 * blue, default field heights) while the v5 half used the dashboard
 * template's theme, so the two halves never matched. Built from the same
 * tokens as the v5 theme so both read as the steerage app.
 */

const { colors: c, radius, shadows: s, table, fonts } = tokens;

// v4 wants 25 elevations; map them onto the three steerage shadows.
const elevations = Array.from({ length: 25 }, (_, i) =>
  i === 0 ? 'none' : i <= 2 ? s.sm : i <= 8 ? s.md : s.lg
) as any;

const legacyTheme = createTheme({
  palette: {
    primary: { main: c.primary, dark: c.primaryDark, light: c.primaryLight, contrastText: '#ffffff' },
    secondary: { main: c.secondary, dark: c.text, light: c.subtle, contrastText: '#ffffff' },
    error: { main: c.danger },
    warning: { main: c.warning },
    info: { main: c.info },
    success: { main: c.success },
    text: { primary: c.text, secondary: c.subtle, disabled: c.muted },
    background: { default: c.bg, paper: c.surface },
    divider: c.border,
    // Tailwind slate, so the panels' and modals' grey header strips
    // (grey[50]) and borders match the steerage app instead of v4's neutrals.
    grey: {
      50: c.bg,
      100: '#f1f5f9',
      200: c.border,
      300: c.borderStrong,
      400: c.muted,
      500: '#64748b',
      600: c.subtle,
      700: c.secondary,
      800: '#1e293b',
      900: c.text,
    },
    action: { hover: c.primarySoft, selected: c.primarySelected },
  },
  typography: {
    fontFamily: fonts.family,
    button: { textTransform: 'none', fontWeight: 500 },
  },
  shape: { borderRadius: radius.sm },
  shadows: elevations,
  overrides: {
    MuiCssBaseline: {
      '@global': {
        body: { backgroundColor: c.bg, color: c.subtle },
      },
    },
    MuiButton: {
      root: { borderRadius: radius.sm },
      contained: { boxShadow: 'none', '&:hover': { boxShadow: 'none' } },
      outlined: { borderColor: c.border },
    },
    MuiOutlinedInput: {
      root: {
        borderRadius: radius.sm,
        '&:hover $notchedOutline': { borderColor: c.muted },
        '&$focused $notchedOutline': { borderColor: c.primary },
      },
      notchedOutline: { borderColor: c.borderStrong },
    },
    MuiPaper: {
      rounded: { borderRadius: radius.md },
    },
    MuiTableCell: {
      head: {
        backgroundColor: table.headBg,
        color: table.headColor,
        fontSize: table.headFontSize,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: table.headLetterSpacing,
        borderBottom: `1px solid ${c.border}`,
      },
      body: {
        color: table.cellColor,
        borderBottom: `1px solid ${c.border}`,
      },
    },
    MuiTableRow: {
      // Steerage tables stripe even rows and tint the row under the pointer.
      root: { '&:nth-of-type(even)': { backgroundColor: table.stripeBg } },
      hover: { '&:hover': { backgroundColor: `${table.hoverBg} !important` } },
    },
    // Steerage cards: hairline border, large corners, faint shadow.
    MuiCard: {
      root: {
        border: `1px solid ${c.border}`,
        borderRadius: radius.lg,
        boxShadow: `${s.sm} !important`,
      },
    },
    MuiChip: {
      root: { borderRadius: radius.sm },
    },
    MuiMenu: {
      paper: { borderRadius: radius.md, boxShadow: s.md },
    },
  },
});

export default legacyTheme;
